import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import Button from 'components/Button';
import Loader from 'components/Loader';
import Notification from 'components/Notification';
import ShopperInformation from './components/ShopperInformation';
import ShopperDetailsForm from './components/ShopperDetailsForm';
import PaymentMethod from './components/PaymentMethod';
import OrderSummary from './components/OrderSummary';
import DonationForm from './components/DonationForm';
import Header from './components/Header';
import { getStoreColors } from './helpers';

import breakpoints from 'utils/breakpoints';
import { captureCurrentStoreId, getStoreById, getTerminalShippingMethods } from 'redux/slices/javelinStore';
import {
  cartSelector,
  getCart,
  cartWithoutAmountSelector,
  editCartProductTemp,
} from 'redux/slices/cart';
import {
  setCheckoutStep,
  getShopperDetails,
  setCheckoutUrl,
} from 'redux/slices/checkout';
import TEXT from 'locales/en/cart';
import { ReactComponent as ShoppingCart } from 'assets/icons/shopping-cart.svg';
import { useMediaQuery } from 'hooks';

const Checkout = () => {
  const cartTotal = useSelector(cartSelector.selectTotal);
  const dispatch = useDispatch();
  const { store, shopperDetails, checkoutStep } = useSelector(
    (state) => state.checkout
  );
  const isLarge = useMediaQuery(breakpoints.lg);
  const dataWithoutAmount = useSelector(cartWithoutAmountSelector);
  const location = useLocation();

  const urlPath = location.pathname.split('/');
  const storeIdFromUrl = urlPath[urlPath.length - 2];
  const clientData = useMemo(() => {
    return store.client?.data || {};
  }, [store.client?.data]);
  const hasHadFundraiserProducts = useSelector(state => state.cart.containsNonFundraiserProducts);

  const editingProduct = useSelector((state) => state.cart.editingProduct);

  const donationProduct = useMemo(
    () => dataWithoutAmount[0] || editingProduct,
    [dataWithoutAmount, editingProduct]
  );

  const initialFetch = useCallback(async () => {
    await dispatch(getCart({ storeId: storeIdFromUrl }));
    if (storeIdFromUrl) {
      await dispatch(getStoreById(storeIdFromUrl));
    }
    dispatch(getShopperDetails({ storeId: storeIdFromUrl }));
    dispatch(setCheckoutUrl(location.pathname));
    dispatch(captureCurrentStoreId(storeIdFromUrl));
    dispatch(getTerminalShippingMethods({ storeId: storeIdFromUrl }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    initialFetch();
  }, [initialFetch]);

  useEffect(() => {
    if (!!donationProduct) {
      dispatch(setCheckoutStep(0));
      if (!editingProduct) {
        dispatch(editCartProductTemp(donationProduct));
      }
    } else if (!shopperDetails.phone_number) {
      dispatch(setCheckoutStep(1));
    } else {
      dispatch(setCheckoutStep(4));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, donationProduct, shopperDetails.phone_number]);

  if (storeIdFromUrl !== store.id || !store.id) return (
    <Loader
      loaderBarClassName={getStoreColors()}
      className={getStoreColors()}
    />
  );

  return (
    <div className="flex lg:flex-col overflow-visible min-h-screen box-border w-full bg-javelin-background">
      <div className="lg:hidden fixed top-0 left-0 px-2 h-16 w-screen bg-white border-b items-center flex justify-center border-gray-200 overflow-y-hidden">
        <p className="ml-0 text-2xl font-semibold text-center truncate">
          {store.name}
        </p>
      </div>
      <Notification className={`dashboard-notification fixed top-4 ${getStoreColors()}`} />
      <div
        className={`w-full ${cartTotal > 0 ? 'flex flex-col-reverse flex-wrap-reverse' : ''
          } lg:grid lg:grid-cols-12`}
      >
        <div className="flex-1 bg-javelin-background lg:bg-white lg:min-h-screen lg:pb-8 lg:border border-javelin-gray-border lg:col-span-6 xl:pl-36 xl:col-span-7 lg:pt-20 lg:px-5">
          <Header
            title={donationProduct ? donationProduct.product.name : store.name}
          />
          {cartTotal > 0 && (
            <div
              data-testid="checkout-details"
              className="lg:pl-0 lg:pr-24 lg:mt-20 border-t border-javelin-gray-border pt-4 px-3 lg:p-0 lg:border-none"
            >
              {checkoutStep > 1 && (
                <div className="lg:mb-12 lg:border border-javelin-inputBorder lg:rounded ">
                  {checkoutStep > 1 && shopperDetails.phone_number && (
                    <ShopperInformation />
                  )}
                </div>
              )}
              {!!donationProduct && <DonationForm product={donationProduct} />}
              {checkoutStep === 1 && (
                <ShopperDetailsForm storeId={storeIdFromUrl} />
              )}
              {checkoutStep === 4 && <PaymentMethod storeId={storeIdFromUrl} />}
            </div>
          )}
        </div>
        {cartTotal > 0 ? (
          !!donationProduct && !isLarge ? (
            <div className="bg-white px-3 mt-16 w-screen lg:hidden h-14 flex items-center place-self-start">
              <p className="truncate font-semibold text-javelin-gray-dark">
                {donationProduct.product.name}
              </p>
            </div>
          ) : (
            <OrderSummary />
          )
        ) : (
          <div className="lg:col-span-6 xl:col-span-4 flex flex-col items-center justify-center lg:h-96 h-112">
            <ShoppingCart className="w-9 h-8 text-javelin-gray-dark fill-current" />
            <h2 className="font-bold text-2xl text-javelin-gray-dark my-5">
              {TEXT.empty}
            </h2>
            <p className="mb-7 text-black text-opacity-30 text-base">
              {TEXT.emptySubtitle}
            </p>
            <Button
              title={hasHadFundraiserProducts ? 'Back to store' : TEXT.browse}
              className="btn-blue w-60 px-0 py-4 block mx-auto text-base"
              onClick={() => window.location.replace(clientData.homepage_url)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Checkout;
